import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Divider,
  Tooltip,
  alpha,
} from "@mui/material";
import { FC, useContext, useState } from "react";
import { AssessmentContext } from "src/context/AssessmentContext";
import { assessmentMethodsDetail } from "./constants";
import { useAppSelector } from "src/app/hooks";
import { useCompanyControllerCheckEraAccessQuery } from "src/app/services/generatedApi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

const methodColors = {
  REBA: "#4caf50",
  RULA: "#2196f3",
  MMH: "#ff9800",
  ERA: "#9c27b0",
  MSD: "#f44336",
  NIOSH: "#ff1356",
};

type SelectAssessmentMethodDialogPropsType = {
  open: boolean;
  handleClose: () => void;
};

export const SelectAssessmentMethodDialog: FC<
  SelectAssessmentMethodDialogPropsType
> = ({ open, handleClose }) => {
  const { selectMethod } = useContext(AssessmentContext);
  const isAdmin = useAppSelector((state) => state.auth?.isAdmin);
  const { data: hasEraAccess } = useCompanyControllerCheckEraAccessQuery();

  const [expandedMethod, setExpandedMethod] = useState<string | null>(null);

  const availableMethods = assessmentMethodsDetail.filter(
    (method) =>
      ((method.method !== "ERA" || hasEraAccess || isAdmin) &&
        method.method !== "MSD" &&
        method.method !== "NIOSH") ||
      isAdmin,
  );

  const handleSelectMethod = (methodName: string) => {
    selectMethod(methodName as any);
    handleClose();
  };

  const toggleExpand = (methodName: string) => {
    setExpandedMethod(expandedMethod === methodName ? null : methodName);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          py: 2,
        }}
      >
        <Typography variant="h6" component="div" fontWeight="medium">
          Select Assessment Method
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        <Grid container spacing={1}>
          {availableMethods.map((method) => {
            const isExpanded = expandedMethod === method.method;
            return (
              <Grid item xs={12} key={method.method}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    borderLeft: `4px solid ${methodColors[method.method] || "#999"}`,
                    "&:hover": {
                      backgroundColor: alpha(
                        methodColors[method.method] || "#999",
                        0.05,
                      ),
                      transform: "translateY(-2px)",
                      boxShadow: 1,
                    },
                    ...(isExpanded && {
                      boxShadow: 2,
                      backgroundColor: alpha(
                        methodColors[method.method] || "#999",
                        0.05,
                      ),
                    }),
                  }}
                >
                  <Box
                    onClick={() => handleSelectMethod(method.method)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {method.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {method.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(method.method);
                          }}
                          sx={{
                            color: isExpanded
                              ? methodColors[method.method] || "primary.main"
                              : "action.active",
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {isExpanded && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        {method.detail}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          alignItems: "end",
        }}
      >
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
